import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select
} from '@mui/material';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { DataGrid } from '@mui/x-data-grid';

import BiMetric from '../../components/biMetric';
import LineChart from '../../components/lineChart';
import MonthDatePicker from '../../components/monthDatePicker';
import PageHeader from '../../components/pageHeader';
import {
    formatBackendDateTimePtBr,
    formatMonthParam,
    getCurrentMonthStart,
    parseBackendDate
} from '../../utils/dateUtils';
import { formatInteger, formatOneDecimal } from '../../utils/numberUtils';
import {
    buildAccessSummary,
    buildClientAccessRows,
    buildDailyClientAccess,
    fetchAcessosMes,
    filterAccessesByPlatform,
    platformOptions
} from './acessosClientesData';
import '../css/biAluguelAccess.css';

const emptyRowsLabel = 'Nenhum acesso encontrado para os filtros selecionados.';

const AcessosClientesBIAluguel = () => {
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthStart);
    const [selectedPlatform, setSelectedPlatform] = useState('ambos');
    const [accesses, setAccesses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadAccesses = useCallback(async (monthValue) => {
        setIsLoading(true);
        const mes = formatMonthParam(monthValue);
        const monthAccesses = await fetchAcessosMes(mes);
        setAccesses(monthAccesses);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadAccesses(selectedMonth);
    }, [loadAccesses, selectedMonth]);

    const filteredAccesses = useMemo(
        () => filterAccessesByPlatform(accesses, selectedPlatform),
        [accesses, selectedPlatform]
    );

    const summary = useMemo(() => buildAccessSummary(filteredAccesses), [filteredAccesses]);
    const dailyAccess = useMemo(
        () => buildDailyClientAccess(filteredAccesses, selectedMonth),
        [filteredAccesses, selectedMonth]
    );
    const rows = useMemo(() => buildClientAccessRows(filteredAccesses), [filteredAccesses]);

    const columns = useMemo(() => [
        {
            field: 'clienteId',
            headerName: 'Cliente ID',
            type: 'number',
            width: 124,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            minWidth: 260,
            flex: 1.4
        },
        {
            field: 'acessos',
            headerName: 'Acessos',
            type: 'number',
            width: 130,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'ultimoAcesso',
            headerName: 'Último acesso',
            minWidth: 190,
            flex: 0.8,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDateTimePtBr(value)
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page'>
            <PageHeader>BI Aluguel - Acessos</PageHeader>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-aluguel-access-overview'>
                    <div className='bi-aluguel-access-filters'>
                        <MonthDatePicker
                            value={selectedMonth}
                            disabled={isLoading}
                            onChange={setSelectedMonth}
                        />

                        <FormControl fullWidth>
                            <InputLabel id='bi-aluguel-access-platform-label'>Plataforma</InputLabel>
                            <Select
                                labelId='bi-aluguel-access-platform-label'
                                label='Plataforma'
                                value={selectedPlatform}
                                disabled={isLoading}
                                onChange={(event) => setSelectedPlatform(event.target.value)}
                            >
                                {platformOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className='bi-aluguel-access-metrics'>
                        <BiMetric
                            className=''
                            icon={GroupsRoundedIcon}
                            label='Clientes que acessaram'
                            value={formatInteger(summary.totalClients)}
                            tone='green'
                        />
                        <BiMetric
                            className=''
                            icon={LoginRoundedIcon}
                            label='Média por cliente'
                            value={formatOneDecimal(summary.averageAccesses)}
                        />
                        <BiMetric
                            className=''
                            icon={RepeatRoundedIcon}
                            label='Clientes recorrentes'
                            value={formatInteger(summary.recurringClients)}
                            tone='blue'
                        />
                    </div>
                </div>

                <LineChart
                    ariaLabel='Clientes que acessaram por dia'
                    eyebrow='Clientes por dia'
                    icon={TrendingUpRoundedIcon}
                    pointAriaLabel={(point) => `${point.label}: ${formatInteger(point.clients)} clientes`}
                    pointKey={(point) => point.day}
                    peakText={(point) => `Pico: ${formatInteger(point.clients)} em ${point.label}`}
                    rows={dailyAccess}
                    title='Acessos no mês'
                    tooltipValue={(point) => `${formatInteger(point.clients)} clientes`}
                    valueKey='clients'
                    xTickLabel={(point) => new Date(`${point.day}T00:00:00`).getDate()}
                />

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Acessos filtrados</p>
                            <h2>Lista do mês</h2>
                        </div>
                        <DevicesRoundedIcon />
                    </div>

                    <div className='bi-aluguel-access-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid'
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 50
                                    }
                                },
                                sorting: {
                                    sortModel: [{ field: 'acessos', sort: 'desc' }]
                                }
                            }}
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default AcessosClientesBIAluguel;
